<template>
  <div class="notifications">
    <div class="notifications__cta-wrapper">
      <p class="notifications__headline">
        {{ $t('HOUSE_BOARD_NOTIFICATION_TITLE') }}
      </p>
      <div v-if="hasNotifications" class="notifications__actions">
        <sh-button
          class="notifications__cta"
          naked="true"
          @click="confirmClearAllNotification"
        >
          {{ $t('HOUSE_BOARD_CLEAR_ALL_NOTIFICATION_CTA') }}
        </sh-button>
        <sh-button
          class="notifications__cta"
          naked="true"
          @click="handleExitNotifications"
        >
          {{ $t('HOUSE_BOARD_NOTIFICATION_SEE_LESS_CTA') }}
        </sh-button>
      </div>
    </div>

    <notification-list
      :notifications="unreadNotifications"
      :isSlideable="$device.isMobileOrTablet"
    />

    <InfiniteScroll
      :spinnerColor="spinnerColor"
      :status="infiniteScrollState"
      :options="infiniteScrollOptions"
      @trigger="handleInfiniteScroll"
    />

    <confirmation-modal
      name="clear-notification-modal"
      :title="$t('HOUSE_BOARD_CLEAR_ALL_NOTIFICATION_LABEL')"
      :cancel="$t('HOUSE_BOARD_CLEAR_ALL_NOTIFICATION_CANCEL')"
      :confirm="$t('HOUSE_BOARD_CLEAR_ALL_NOTIFICATION_CONFIRM')"
      @cancel="closeModal"
      @confirm="confirmModal"
    />
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import { ShBlade, InfiniteScroll } from '@sohohouse/design-system'
import { decorators } from '@sohohouse/platform-resources'
import designTokens from '@sohohouse/design-system/dist/tokens/tokens.json'

import UIMixin from '~/mixins/ui'
import NotificationList from '~/components/notification-list'
import { VIEWS } from '~/constants/house-board-views'
import ConfirmationModal from '~/components/modal/confirmation'

const { mapActions: mapModalsActions } = createNamespacedHelpers('ui/modals')
const {
  mapGetters: mapNotificationGetters,
  mapActions: mapNotificationActions
} = createNamespacedHelpers('notifications')

const { DateTime } = decorators.shared

export default {
  components: {
    ShBlade,
    NotificationList,
    InfiniteScroll,
    ConfirmationModal
  },

  mixins: [
    UIMixin
  ],

  data () {
    return {
      infiniteScrollState: 'ready',
      spinnerColor: designTokens.color_white,
      infiniteScrollOptions: {
        root: null,
        threshold: 0
      }
    }
  },

  async fetch ({ store }) {
    await store.dispatch('notifications/fetchNotifications')
  },

  computed: {
    ...mapNotificationGetters([
      'unreadNotifications',
      'showNotificationAlert',
      'pageCursorNext'
    ]),

    hasNotifications () {
      return this.unreadNotifications.length > 0
    },

    isLastPage () {
      return this.pageCursorNext === null
    }
  },

  watch: {
    hasNotifications: 'handleExitNotifications',
    showNotificationAlert: {
      handler: 'setUnseenNotifications',
      immediate: true
    }
  },

  methods: {
    ...mapModalsActions(['close']),
    ...mapNotificationActions([
      'markNotificationsAsSeen',
      'dismissNotificationGroup',
      'dismissNotification',
      'fetchNotifications'
    ]),

    async handleInfiniteScroll () {
      try {
        this.infiniteScrollState = 'loading'
        await this.fetchNotifications({
          isPaginating: true, params: { page: { cursor: this.pageCursorNext } }
        })
        this.infiniteScrollState = this.isLastPage ? 'complete' : 'ready'
      } catch (error) {
        this.infiniteScrollState = 'error'
      }
    },

    dateToHour (t) {
      const d = new DateTime(t)

      return this.$d(d.withOffset.epoch, 'time')
    },

    closeHouseBoard () {
      this.$ui.modal.close(this.name)
    },

    confirmClearAllNotification () {
      this.$ui.modal.open('clear-notification-modal')
    },

    closeModal () {
      this.$ui.modal.close('clear-notification-modal')
    },

    confirmModal () {
      const [notification] = this.unreadNotifications
      this.dismissNotificationGroup(notification.notificationGroup)
      this.closeModal()
    },

    handleExitNotifications () {
      this.$emit('handleViewChange', VIEWS.HOUSE_BOARD)
    },

    setUnseenNotifications () {
      if (
        this.showNotificationAlert
        && this.unreadNotifications
        && this.unreadNotifications.length > 0
      ) {
        const [notification] = this.unreadNotifications
        this.markNotificationsAsSeen(notification.notificationGroup)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.notifications {
  height: min-content;
  overflow-y: scroll;

  &__headline {
    color: $color-white;

    @include font('060');
  }

  &__cta-wrapper {
    display: flex;
    justify-content: space-between;
    margin-bottom: $space-030;

    @include font('040');
  }

  &__cta {
    color: $color-white !important;

    &:first-child {
      margin-right: $space-020;
    }
  }

  &__actions {
    display: flex;
  }
}
</style>
